import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { i18WithParams as t } from '@shared/locales'
import { formatInTimeZone } from 'date-fns-tz'
import { useMemo, useState } from 'react'

import { formatNumberAsPrice } from '@shared/common'
import { InvoiceStatusChip } from 'invoice_status_chip/invoice_status_chip'
import { Link as NavLink, useParams } from 'react-router-dom'
import { Types } from 'service-api'
import { DEFAULT_TIMEZONE, getInvoiceSubStatus, isSchedulingInvoice, isVisitInvoice } from 'shared'
import { CancelInvoiceModal } from './cancel_invoice'
import { canCancelInvoice, invoiceCancelDateTime, invoiceDates, invoiceServiceName } from './helper'
import { InvoiceStatus } from './invoice_status'
import { getInvoiceRefundTotal } from '@shared/payment'

const DEFAULT_DATE_FORMAT = 'MMM d, yyyy'

export type RefundSetting = {
  id: string
  before: number
  after: number
  order: number
  rate: string
  status: string
}

type Props = {
  invoice: Types.CustomInvoiceAdminDto
  order: Types.CustomOrderAdminDto
  isAdmin?: boolean
  canAccessOS?: boolean
  onCancelInvoice: () => void
  onPreCancelInvoice?: () => Promise<void>
}

export const InvoiceCard = ({ invoice, order, isAdmin, canAccessOS, onCancelInvoice, onPreCancelInvoice }: Props) => {
  const theme = useTheme()
  const { accountName: locationName = '' } = useParams()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const timezone = invoice.location?.timezone || DEFAULT_TIMEZONE
  const isGroom = invoice.locationServiceType?.serviceType?.name === 'grooming'
  const isVisit = isVisitInvoice(invoice)
  const isScheduling = isSchedulingInvoice(invoice)
  const invoiceSubStatus = getInvoiceSubStatus(invoice)

  const cancelButton = useMemo(() => {
    const label =
      isScheduling && invoiceSubStatus.isRequested ? 'global.label.cancelRequest' : 'global.label.cancelBooking'
    return canCancelInvoice(invoice, order, !!isAdmin) ? (
      <Box>
        <Button variant="outlined" color="error" size="small" onClick={() => setCancelModalOpen(true)}>
          {t(label)}
        </Button>
      </Box>
    ) : null
  }, [isScheduling, invoiceSubStatus.isRequested, invoice, order, isAdmin])

  const invoiceName = useMemo(() => invoiceServiceName(invoice), [invoice])
  const invoiceDate = useMemo(() => invoiceDates(invoice), [invoice])
  const cancelBy = useMemo(() => invoiceCancelDateTime(invoice, order), [invoice, order])
  const invoiceDatePrefix = useMemo(() => {
    if (isScheduling && !invoiceSubStatus.isCanceled && !invoiceSubStatus.isDeclined) {
      return invoiceSubStatus.isScheduled || invoiceSubStatus.isInProgress || invoiceSubStatus.isCheckedOut
        ? t('invoiceCard.scheduled')
        : t('invoiceCard.requested')
    }
    return ''
  }, [isScheduling, invoiceSubStatus])

  const cancellationDetails = useMemo(() => {
    const { canceledAt, cancellationReason } = invoice
    const date = canceledAt ? formatInTimeZone(canceledAt, timezone, DEFAULT_DATE_FORMAT) : ''
    switch (cancellationReason) {
      case 'USER': {
        return t('invoiceCard.cancelByCustomer', { date })
      }
      case 'LOCATION': {
        return t('invoiceCard.cancelByStaff', { date })
      }
      default: {
        return t('invoiceCard.cancelByOther', { date })
      }
    }
  }, [invoice, timezone])

  const tilledLink = useMemo(() => {
    const existingPayment = order.payments?.[0] || {}
    const env = import.meta.env.VITE_TILLED_ENVIRONMENT
    const tilledDomain =
      env === 'prod' ? `https://goose.paymentsonline.io` : `https://goose-${env}.sandbox-paymentsonline.io`
    const linkURL = existingPayment.merchantId
      ? `${tilledDomain}/${existingPayment.merchantId}/payments/${existingPayment.paymentIntentId}`
      : ''
    return isAdmin &&
      existingPayment.merchantId &&
      existingPayment.paymentIntentId &&
      existingPayment.payFacType === 'TILLED' ? (
      <Button variant="outlined" size="small" target="_blank" href={linkURL}>
        {t('invoiceCard.viewPaymentTransaction')}
      </Button>
    ) : null
  }, [order.payments, isAdmin])

  const invoiceOSLink = useMemo(() => {
    if (!isAdmin || !canAccessOS) {
      return null
    }
    return (
      <Button component={NavLink} to={`/v2/${locationName}/invoice/${invoice.id}`} variant="outlined">
        {t('invoiceCard.viewInvoiceInOS')}
      </Button>
    )
  }, [isAdmin, canAccessOS, locationName, invoice.id])

  const refundTotal = getInvoiceRefundTotal(invoice.id, order.payments)

  return (
    <>
      <Paper sx={{ borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
        <Box p={2}>
          <Stack gap={1} direction={'row'} justifyContent="space-between" alignItems="center" flexWrap="wrap">
            <Stack direction={isBelowMD ? 'column' : 'row'} spacing={2} alignItems={isBelowMD ? 'start' : 'center'}>
              {isGroom && !invoiceSubStatus.isInProgress && !invoiceSubStatus.isCheckedOut ? (
                <InvoiceStatusChip invoice={invoice} />
              ) : (
                <InvoiceStatus invoice={invoice} />
              )}
              <Stack>
                {!isGroom && (
                  <Typography variant="body1">
                    {t('invoiceCard.confirmationNum', {
                      num: invoice?.name?.toString() ?? '',
                    })}
                  </Typography>
                )}
                {invoice.invoiceStatus === 'CANCELED' && invoice.canceledAt && (
                  <Typography variant="body2">{`${cancellationDetails}`}</Typography>
                )}
              </Stack>
            </Stack>
            {isVisit && !cancelButton && (
              <Stack>
                <Typography variant="body2" sx={{ textAlign: 'right' }}>
                  {t('invoiceCard.deposit', { deposit: formatNumberAsPrice(invoice.charged || 0) })}
                </Typography>
                {refundTotal ? (
                  <Typography variant="body2bold" textAlign="right" sx={{ color: 'error.main' }}>
                    {t('invoiceCard.refunded', {
                      total: formatNumberAsPrice(refundTotal),
                    })}
                  </Typography>
                ) : null}
              </Stack>
            )}
            {isVisit && cancelButton}
          </Stack>
          {isBelowMD ? (
            <Box sx={{ mt: 4 }}>
              <Stack mb={1}>
                <Typography variant="caption">{t('global.label.for')}</Typography>
                <Typography variant="body2">{invoiceName}</Typography>
                <Typography variant="body2">{`${invoiceDatePrefix} ${invoiceDate}`}</Typography>
              </Stack>
              <Stack direction={'row'} mb={1}>
                <Stack flexGrow={1}>
                  <Typography variant="caption">
                    {t(isGroom ? 'global.label.requestedOn' : 'global.label.reservedOn')}
                  </Typography>
                  <Typography variant="body2">
                    {formatInTimeZone(invoice.createdAt!, timezone, DEFAULT_DATE_FORMAT)}
                  </Typography>
                </Stack>
                <Stack flexGrow={1}>
                  <Typography variant="caption">{t('global.label.cancelBy')}</Typography>
                  <Typography variant="body2">{cancelBy}</Typography>
                </Stack>
              </Stack>
            </Box>
          ) : (
            <TableContainer sx={{ mt: 4 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('global.label.for')}</TableCell>
                    <TableCell>{t(isGroom ? 'global.label.requestedOn' : 'global.label.reservedOn')}</TableCell>
                    <TableCell>{t('global.label.cancelBy')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{ verticalAlign: 'baseline' }}>
                    <TableCell width="40%">
                      <Typography variant="body2">{invoiceName}</Typography>
                      <Typography variant="body2">{`${invoiceDatePrefix} ${invoiceDate}`}</Typography>
                    </TableCell>
                    <TableCell width="30%">
                      {formatInTimeZone(invoice.createdAt!, timezone, DEFAULT_DATE_FORMAT)}
                    </TableCell>
                    <TableCell>{cancelBy}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Stack direction="row" spacing={2} mt={4}>
            {tilledLink}
            {invoiceOSLink}
          </Stack>
        </Box>
      </Paper>
      <CancelInvoiceModal
        invoice={invoice}
        order={order}
        isAdmin={isAdmin}
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onPreCancelInvoice={onPreCancelInvoice}
        onCancelInvoice={onCancelInvoice}
      />
    </>
  )
}
